import useSearch from "@/apis/search";
import useFilter, { FilterState } from "@/hooks/useFilter";
import { useQuery } from "@tanstack/react-query";
import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

interface FilterData {
  medium: FilterItem[];
  surface: FilterItem[];
  movements: FilterItem[];
  objectTypes: ObjectTypeItem[];
  materials: FilterItem[];
  years: FilterItem[];
  cities: FilterItem[];
  artistName: FilterItem[];
  locations: FilterItem[];
  possession: FilterItem[];
  protect: ProtectItem[];
  registration: FilterItem[];
  filteredResults: FilteredResult[];
  maxSize: MaxSize;
  authorName: FilterItem[];
  tags: FilterItem[];
  eventType: FilterItem[];
}

interface FilterItem {
  label: string;
  count: string;
}

interface ObjectTypeItem extends FilterItem {
  priority: number;
}

interface ProtectItem extends FilterItem {
  id: string;
}

interface FilteredResult {
  _id: string;
}

interface MaxSize {
  height: Measurement;
  width: Measurement;
  depth: Measurement;
  diameter: Measurement;
}

interface Measurement {
  cm: number;
  inch: number;
}

interface SearchContextType {
  searchFilter: FilterState;
  filterList?: FilterData; // Make this optional
  displayData: any;
  displayDataLoading: boolean;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);
const formatCategory = (value: string) => {
  if (value === "all") {
    return "";
  } else if (value === "artists") {
    return "artist";
  } else if (value === "object") {
    return "object";
  } else if (value === "businesses") {
    return "business";
  } else if (value === "videos") {
    return "video";
  } else if (value === "events") {
    return "event";
  } else if (value === "writing") {
    return "writing";
  }
  return value;
};

export function extractData(data: any) {
  const result: { event?: any[]; [key: string]: any } = { event: [] };

  data?.forEach((item: any) => {
    if (item.eventName) {
      result.event?.push(item);
    } else if (item.userRole) {
      result[item.userRole] ??= [];
      result[item.userRole].push(item);
    } else if (item.contentType === "video" || item.contentType === "writing") {
      result[item.contentType] ??= [];
      result[item.contentType].push(item);
    } else {
      result[item.category] ??= [];
      result[item.category].push(item);
    }
  });

  return result;
}

export const SearchContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { filterData, searchData } = useSearch();
  const [searchParams] = useSearchParams();
  const search = searchParams.get("q");
  const searchCategroy = searchParams?.get("category");
  const searchFilter = useFilter({
    query: {
      searchText: search,
      category: searchCategroy == "all" ? "" : searchCategroy,
    },
    options: {
      limit: 10,
      page: 1,
    },
  });

  const { isLoading: displayDataLoading, data: searchDataResult } = useQuery({
    queryKey: ["searchData", searchFilter.filterOption, searchCategroy, search],
    queryFn: () => searchData(searchFilter.filterOption),
    enabled: !!searchFilter?.filterOption?.options && !!search,
  });

  // Extract and format the search data
  const displayData = useMemo(() => {
    return extractData(searchDataResult);
  }, [searchDataResult]);

  // Only use serializable values in queryKey
  const { data: filterList } = useQuery({
    queryKey: [
      "searchData",
      searchFilter.filterOption?.query?.type,
      searchFilter.filterOption?.query?.artistName,
      searchFilter.filterOption?.query?.creationYear,
      searchFilter.filterOption?.query?.searchQuery,
      searchFilter.filterOption?.query?.size,
      searchFilter.filterOption?.query?.cities,
      searchFilter.filterOption?.query?.possession,
      searchFilter.filterOption?.query?.protect,
      searchFilter.filterOption?.query?.registration,
      searchFilter.filterOption?.query?.medium,
      searchFilter.filterOption?.query?.surface,
      searchCategroy,
      search,
    ],
    queryFn: () =>
      filterData(
        {
          searchQuery: search,
          type: searchFilter.filterOption?.query?.type,
          cities: searchFilter.filterOption?.query?.cities,
          possession: searchFilter.filterOption?.query?.possession,
          artistName: searchFilter.filterOption?.query?.artistName,
          creationYear: searchFilter.filterOption?.query?.creationYear,
          protect: searchFilter.filterOption?.query?.protect,
          registration: searchFilter.filterOption?.query?.registration,
          size: searchFilter.filterOption?.query?.size,
          medium: searchFilter.filterOption?.query?.medium,
          surface: searchFilter.filterOption?.query?.surface,
        },
        formatCategory(searchCategroy ?? "object")
      ),
    enabled:
      !!searchFilter.filterOption &&
      !!searchCategroy &&
      [
        "objects",
        "artists",
        "businesses",
        "videos",
        "events",
        "writing",
      ].includes(searchCategroy),
    staleTime: 1000 * 60 * 60 * 24, //do not the api for next 24 hours
  });

  useEffect(() => {
    console.log("Initial Search:", search);
    console.log("Initial Category:", searchCategroy);
    console.log("Filter Option:", searchFilter.filterOption);
  }, [search, searchCategroy, searchFilter.filterOption]);

  useEffect(() => {
    if (
      searchFilter?.filterOption?.query?.searchText !== search ||
      searchFilter?.filterOption?.query?.category !==
        (searchCategroy == "all" ? "" : searchCategroy)
    ) {
      searchFilter.setFilterOption({
        ...searchFilter.filterOption,
        query: {
          ...searchFilter.filterOption.query,
          searchText: search,
          category: searchCategroy == "all" ? "" : searchCategroy,
        },
      });
    }
  }, [search, searchCategroy]);
  return (
    <SearchContext.Provider
      value={{ searchFilter, filterList, displayData, displayDataLoading }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error(
      "useSearchContext must be used within a SearchContextProvider"
    );
  }
  return context;
};
